import { useTranslation } from 'react-i18next'
import { Outlet, useMatch, useNavigate, useParams } from 'react-router'
import { useCallback, useMemo } from 'react'
import { EcommerceCommonHeader } from '@/components/shared/headers'
import { PageBody, PageHeader } from '@/components/shared/page-sections'
import { ScrollableRouteContainer } from '@/components/shared/Container'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'
import { ModuleNavigation, TabValue } from '@/components/shared/ModuleNavigation'
import { Show } from '@/components/shared/show'
import { useCapitalStructureStore } from '@/feature/financial-model/ecommerce/capital-structure/store'
import { ExportModuleDialog } from '@/feature/export/ecommerce/components/dowload'
import { ViewSwitcher } from '@/components/shared/views-buttons'
import { useCapitalStructureStatus } from '@/feature/financial-model/ecommerce/capital-structure/hooks/useCapitalStructureStatus'

const CapitalStructureRoute = () => {
  const { id: financialModelId } = useParams<{ id: string }>()
  const { isEquitySetupCompleted, isLoading } = useCapitalStructureStatus(financialModelId!)
  const financialModelName = useFinancialModelSettingsStore(state => state.settings[financialModelId!].general?.name)
  const navigate = useNavigate()
  const handleTabsChange = useCallback(
    (value: TabValue) => {
      const tabNavigationMap: Record<TabValue, string> = {
        'set-up': `/dashboard/financial-model/${financialModelId}/ecommerce/capital-structure`,
        'historical-data': `/dashboard/financial-model/${financialModelId}/ecommerce/capital-structure/historical-data`,
        'forecast-data': `/dashboard/financial-model/${financialModelId}/ecommerce/capital-structure/forecast-data`,
        total: `/dashboard/financial-model/${financialModelId}/ecommerce/capital-structure/total`
      }
      navigate(tabNavigationMap[value])
    },
    [financialModelId, navigate]
  )

  const isCapitalStructureTotalsRoute = useMatch({
    path: `/dashboard/financial-model/${financialModelId}/ecommerce/capital-structure/total`
  })

  // Hardcoded setup completion as requested
  const isSetupCompleted = !isLoading && isEquitySetupCompleted

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <Show
        when={!!isCapitalStructureTotalsRoute}
        fallback={<SetupPageHeader financialModelName={financialModelName!} financialModelId={financialModelId!} />}
      >
        <TotalsPageHeader financialModelName={financialModelName!} financialModelId={financialModelId!} />
      </Show>

      <ModuleNavigation isSetupCompleted={isSetupCompleted} handleTabsChange={handleTabsChange} isTotalAllowed={isSetupCompleted}>
        <PageBody>
          <Outlet />
        </PageBody>
      </ModuleNavigation>
    </ScrollableRouteContainer>
  )
}

const SetupPageHeader = (props: { financialModelName: string; financialModelId: string }) => {
  const { t } = useTranslation(`capitalStructure`)
  const { financialModelName, financialModelId } = props
  return (
    <PageHeader className={`pb-0`}>
      <EcommerceCommonHeader linkText={t(`common.header.back`)} path={`/dashboard/financial-model/${financialModelId}/ecommerce`} />
      <div className='mt-6'>
        <h1 className='page-title'>{t(`common.header.title`)}</h1>
        <p className='page-subtitle'>{financialModelName}</p>
      </div>
    </PageHeader>
  )
}

const TotalsPageHeader = (props: { financialModelName: string; financialModelId: string }) => {
  const { t } = useTranslation(`capitalStructure`)
  const { financialModelName, financialModelId } = props

  const view = useCapitalStructureStore(state => state.view)
  const setView = useCapitalStructureStore(state => state.setView)

  const exportOptions = useMemo(
    () => ({
      module: `capital-structure` as const,
      view
    }),
    [view]
  )

  return (
    <PageHeader className={`pb-0`}>
      <EcommerceCommonHeader linkText={t(`common.header.back`)} path={`/dashboard/financial-model/${financialModelId}/ecommerce`} />
      <div className='mt-6 flex items-center justify-between'>
        <div>
          <h1 className='page-title'>{t(`totals.title`)}</h1>
          <p className='page-subtitle'>{financialModelName}</p>
        </div>
        <div className='flex items-center gap-2'>
          <ExportModuleDialog financialModelId={financialModelId!} options={exportOptions} />
          <ViewSwitcher view={view} onViewChange={setView} />
        </div>
      </div>
    </PageHeader>
  )
}

export default CapitalStructureRoute
